import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Globe, Star, Ban, ShieldCheck, Unplug } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDate } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = {
  ACTIVE: "bg-emerald-500/15 text-emerald-400",
  VERIFIED: "bg-sky-500/15 text-sky-400",
  PENDING_VERIFICATION: "bg-amber-500/15 text-amber-400",
  SUSPENDED: "bg-rose-500/15 text-rose-400",
  DISCONNECTED: "bg-slate-600/30 text-slate-500",
};

const Chip = ({ label, style, testid }) => (
  <span data-testid={testid} className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${style}`}>{label}</span>
);

export default function AdminDomains() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-domains"],
    queryFn: async () => (await api.get("/admin/domains")).data,
  });

  const act = useMutation({
    mutationFn: async ({ id, action }) => (action === "disconnect"
      ? api.delete(`/admin/domains/${id}`)
      : api.post(`/admin/domains/${id}/${action}`, {})),
    onSuccess: (_d, v) => { toast.success(`Domain ${v.action}d`); qc.invalidateQueries({ queryKey: ["admin-domains"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const items = data?.items || [];

  return (
    <div data-testid="admin-domains-page">
      <PageHeader title="Custom Domains" sub="Agent white-label domains — verify, activate, suspend or disconnect. Suspended domains stop resolving immediately; the DropPartner store URL keeps working." testid="admin-domains-title" />

      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : !items.length ? (
        <EmptyState title="No custom domains yet" sub="Agents connect domains from My Store → Domain tab." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-800" data-testid="domains-table">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Domain</th>
                <th className="px-4 py-3">Store</th>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Verification</th>
                <th className="px-4 py-3">SSL</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} data-testid={`domain-row-${d.domain}`} className="border-b border-slate-800/60">
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 font-mono font-semibold text-slate-100">
                      <Globe size={12} className="text-slate-500" /> {d.domain}
                      {d.is_primary && <Chip label="PRIMARY" style="bg-orange-500/15 text-orange-400" testid={`primary-${d.domain}`} />}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{d.store_name || d.store_slug || "—"}</td>
                  <td className="px-4 py-3 text-slate-300">{d.agent_name || "—"}<p className="text-[10px] text-slate-600">{d.agent_email}</p></td>
                  <td className="px-4 py-3 text-slate-400">{d.domain_type === "SUBDOMAIN" ? "Subdomain" : "Root/Custom"}</td>
                  <td className="px-4 py-3"><Chip label={d.status} style={STATUS_STYLE[d.status] || "bg-slate-700/40 text-slate-300"} testid={`status-${d.domain}`} /></td>
                  <td className="px-4 py-3"><Chip label={d.verification_status} style={d.verification_status === "VERIFIED" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"} testid={`verification-${d.domain}`} /></td>
                  <td className="px-4 py-3"><Chip label={d.ssl_status} style={d.ssl_status === "ACTIVE" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700/40 text-slate-300"} testid={`ssl-${d.domain}`} /></td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(d.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      {d.status !== "ACTIVE" && d.status !== "DISCONNECTED" && (
                        <Button data-testid={`activate-${d.domain}`} size="sm" variant="outline"
                          onClick={() => act.mutate({ id: d.id, action: "activate" })}
                          className="h-7 border-emerald-500/40 text-[10px] text-emerald-400 hover:bg-emerald-500/10">
                          <ShieldCheck size={11} /> Activate
                        </Button>
                      )}
                      {d.status === "ACTIVE" && (
                        <Button data-testid={`suspend-${d.domain}`} size="sm" variant="outline"
                          onClick={() => act.mutate({ id: d.id, action: "suspend" })}
                          className="h-7 border-amber-500/40 text-[10px] text-amber-400 hover:bg-amber-500/10">
                          <Ban size={11} /> Suspend
                        </Button>
                      )}
                      {!d.is_primary && d.status === "ACTIVE" && (
                        <Button data-testid={`set-primary-${d.domain}`} size="sm" variant="outline"
                          onClick={() => act.mutate({ id: d.id, action: "primary" })}
                          className="h-7 border-slate-700 text-[10px] text-slate-300 hover:bg-slate-800">
                          <Star size={11} /> Primary
                        </Button>
                      )}
                      {d.status !== "DISCONNECTED" && (
                        <Button data-testid={`disconnect-${d.domain}`} size="sm" variant="outline"
                          onClick={() => { if (window.confirm(`Disconnect ${d.domain}? The store keeps working at its DropPartner URL.`)) act.mutate({ id: d.id, action: "disconnect" }); }}
                          className="h-7 border-rose-500/40 text-[10px] text-rose-400 hover:bg-rose-500/10">
                          <Unplug size={11} /> Disconnect
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
